import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtEUR, fmtUSD, fmtPct, newItem } from "@/pages/motor/shared";

const Row = ({ label, children, strong, muted }) => (
  <div className="flex items-center justify-between">
    <span className={`${muted ? "text-muted-foreground" : ""} ${strong ? "font-semibold" : ""}`.trim()}>{label}</span>
    <span className={`tabular-nums ${strong ? "font-semibold" : ""}`.trim()}>{children}</span>
  </div>
);

export const Calculadora = ({ onSaved }) => {
  const qc = useQueryClient();
  const partnersQ = useQuery({
    queryKey: ["formula-partners"],
    queryFn: async () => (await api.get("/formulas/partners")).data,
  });
  const partners = partnersQ.data?.partners || [];
  const globals = partnersQ.data?.globals || {};

  const [partner, setPartner] = useState("kimkim");
  const [fxRate, setFxRate] = useState("");
  const [paypalFee, setPaypalFee] = useState(false);
  const [items, setItems] = useState([newItem()]);
  const [result, setResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const [saveOpen, setSaveOpen] = useState(false);
  const [form, setForm] = useState({ name: "", expected_pvp_eur: "", trip_year: 2026, notes: "" });

  useEffect(() => {
    if (globals.default_fx_eur_usd && fxRate === "") setFxRate(String(globals.default_fx_eur_usd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globals.default_fx_eur_usd]);

  const payload = useMemo(() => ({
    partner,
    fx_rate: fxRate === "" ? null : parseFloat(fxRate),
    paypal_fee: paypalFee,
    items: items.map((it) => ({
      description: it.description,
      country: it.country,
      cost_tax_excl: parseFloat(it.cost_tax_excl) || 0,
      cost_tax_incl: parseFloat(it.cost_tax_incl) || 0,
      qty: parseFloat(it.qty) || 0,
    })),
  }), [partner, fxRate, paypalFee, items]);

  const runCalc = useCallback(async () => {
    try {
      setCalcLoading(true);
      const { data } = await api.post("/formulas/calc", payload);
      setResult(data.result);
    } catch (e) {
      /* preview silencioso */
    } finally {
      setCalcLoading(false);
    }
  }, [payload]);

  useEffect(() => {
    const t = setTimeout(runCalc, 350);
    return () => clearTimeout(t);
  }, [runCalc]);

  const updateItem = (idx, key, val) => setItems((s) => s.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const removeItem = (idx) => setItems((s) => (s.length > 1 ? s.filter((_, i) => i !== idx) : s));
  const addItem = () => setItems((s) => [...s, newItem()]);

  const saveMut = useMutation({
    mutationFn: () => api.post("/quotes", {
      ...payload,
      name: form.name,
      expected_pvp_eur: form.expected_pvp_eur === "" ? null : parseFloat(form.expected_pvp_eur),
      trip_year: form.trip_year ? parseInt(form.trip_year, 10) : null,
      notes: form.notes,
    }),
    onSuccess: () => {
      toast.success("Cotización guardada");
      qc.invalidateQueries({ queryKey: ["quotes"] });
      setSaveOpen(false);
      setForm({ name: "", expected_pvp_eur: "", trip_year: 2026, notes: "" });
      onSaved && onSaved();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const totals = result?.totals;
  const lines = result?.lines || [];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="calculadora">
      <div className="space-y-4 lg:col-span-2">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Datos de la cotización</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Partner / Fuente</Label>
                <Select value={partner} onValueChange={setPartner}>
                  <SelectTrigger data-testid="partner-select"><SelectValue placeholder="Partner" /></SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cambio €→$ (manual)</Label>
                <Input data-testid="fx-rate-input" type="number" step="0.0001" value={fxRate} onChange={(e) => setFxRate(e.target.value)} placeholder="1.08" />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2">
                <div><Label className="text-xs">Recargo PayPal +3%</Label><p className="text-[11px] text-muted-foreground">Opcional</p></div>
                <Switch checked={paypalFee} onCheckedChange={setPaypalFee} data-testid="paypal-switch" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base">Servicios</CardTitle>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-button"><Plus className="mr-1 h-4 w-4" />Añadir servicio</Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.map((it, idx) => {
              const line = lines[idx];
              return (
                <div key={idx} className="rounded-lg border p-3" data-testid={`item-row-${idx}`}>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-12">
                    <div className="space-y-1 sm:col-span-4">
                      <Label className="text-[11px]">Descripción</Label>
                      <Input value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Hotel, tour…" data-testid={`item-desc-${idx}`} />
                    </div>
                    <div className="space-y-1 sm:col-span-3">
                      <Label className="text-[11px]">País</Label>
                      <Input value={it.country} onChange={(e) => updateItem(idx, "country", e.target.value)} placeholder="España, Italia…" data-testid={`item-country-${idx}`} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-[11px]">Coste sin IVA</Label>
                      <Input type="number" step="0.01" value={it.cost_tax_excl} onChange={(e) => updateItem(idx, "cost_tax_excl", e.target.value)} data-testid={`item-excl-${idx}`} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-[11px]">Coste con IVA</Label>
                      <Input type="number" step="0.01" value={it.cost_tax_incl} onChange={(e) => updateItem(idx, "cost_tax_incl", e.target.value)} data-testid={`item-incl-${idx}`} />
                    </div>
                    <div className="space-y-1 sm:col-span-1">
                      <Label className="text-[11px]">Cant.</Label>
                      <Input type="number" step="1" value={it.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} data-testid={`item-qty-${idx}`} />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {line && (
                        <>
                          <Badge variant="outline" className="text-[10px]">{line.is_spain ? "España · base SIN IVA" : "Extranjero · base CON IVA"}</Badge>
                          <span>Cotización: <span className="font-medium tabular-nums text-foreground" data-testid={`item-quoted-${idx}`}>{fmtEUR(line.line_quoted)}</span></span>
                        </>
                      )}
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(idx)} disabled={items.length <= 1} data-testid={`remove-item-${idx}`} aria-label="Eliminar servicio">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="sticky top-20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              Resultado {calcLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {!totals ? (
              <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" />)}</div>
            ) : (
              <>
                <Row label="Coste sin IVA">{fmtEUR(totals.cost_tax_excl)}</Row>
                <Row label="Coste con IVA">{fmtEUR(totals.cost_tax_incl)}</Row>
                <Row label="IVA (informativo)" muted>{fmtEUR(totals.iva_info)}</Row>
                <Separator />
                <Row label="Markup">+ {fmtEUR(totals.markup_eur)}</Row>
                {totals.uplift_eur > 0 && <Row label="KimKim +8%">+ {fmtEUR(totals.uplift_eur)}</Row>}
                <Row label="Subtotal cotización" strong>{fmtEUR(totals.subtotal_quoted)}</Row>
                {totals.commission_eur > 0 && <Row label="Comisión partner">+ {fmtEUR(totals.commission_eur)}</Row>}
                {totals.paypal_eur > 0 && <Row label="Recargo PayPal">+ {fmtEUR(totals.paypal_eur)}</Row>}
                <Separator />
                <div className="flex items-center justify-between rounded-lg bg-primary/5 px-3 py-2">
                  <span className="font-semibold">PVP (€)</span>
                  <span className="text-lg font-bold tabular-nums text-primary" data-testid="result-pvp-eur">{fmtEUR(totals.pvp_eur)}</span>
                </div>
                <Row label="PVP ($)" muted><span data-testid="result-pvp-usd">{fmtUSD(totals.pvp_usd)}</span></Row>
                <Row label="Margen bruto" muted>{fmtEUR(totals.margin_eur)} · {fmtPct(totals.margin_pct)}</Row>
              </>
            )}
            <Button className="mt-2 w-full" onClick={() => setSaveOpen(true)} disabled={!totals} data-testid="open-save-quote">
              <Save className="mr-1.5 h-4 w-4" /> Guardar cotización
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-quote-dialog">
          <DialogHeader>
            <DialogTitle>Guardar cotización</DialogTitle>
            <DialogDescription>Se anclará a la versión de fórmula activa. Para validar, indica el PVP real del viaje.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nombre del viaje</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Familia López · Italia 2026" data-testid="save-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>PVP real esperado (€)</Label><Input type="number" step="0.01" value={form.expected_pvp_eur} onChange={(e) => setForm({ ...form, expected_pvp_eur: e.target.value })} placeholder="opcional" data-testid="save-expected-input" /></div>
              <div className="space-y-1.5"><Label>Año</Label><Input type="number" value={form.trip_year} onChange={(e) => setForm({ ...form, trip_year: e.target.value })} data-testid="save-year-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="opcional" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="save-quote-button">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
